import * as React from "react";
import { FC, useEffect } from "react";
import { Property } from "csstype";

import { useInstanceIndex } from "../../Internals/ControlContexts/PathContexts/InstanceIndexContext";

import { ColorNumber } from "./InstanceNumber.styles";

export interface InstanceNumberProps {
    parentInstanceNumber?: number;
    useParentAsPrefix?: boolean;
    prefix?: string;
    start: number;
    step: number;
    maxValue?: number;
    minLength?: number;
    color?: Property.Color;

    onInstanceNumberAdded: (instanceNumber: number) => void;
    onInstanceNumberRemoved: () => void;
}

export const InstanceNumber: FC<InstanceNumberProps> = props => {
    const instanceIndex = useInstanceIndex();
    const instanceNumber = calcInstanceNumber(props, instanceIndex);

    useEffect(() => {
        props.onInstanceNumberAdded(Number(instanceNumber));

        return props.onInstanceNumberRemoved;
    }, [instanceNumber]);

    return <ColorNumber color={props.color}>{instanceNumber}</ColorNumber>;
};

function calcInstanceNumber(props: InstanceNumberProps, instanceIndex: number) {
    if (props.useParentAsPrefix && props.parentInstanceNumber !== undefined) {
        return combineInstanceNumberWithPrefix(props.parentInstanceNumber, instanceIndex);
    }

    if (props.prefix !== undefined) {
        return combineInstanceNumberWithPrefix(props.prefix, instanceIndex);
    }

    return calcInstanceNumberFromStart(props, instanceIndex);
}

function combineInstanceNumberWithPrefix(prefix: string | number, instanceIndex: number) {
    return `${prefix}${instanceIndex + 1}`;
}

function calcInstanceNumberFromStart({ start, step, maxValue, minLength }: InstanceNumberProps, instanceIndex: number) {
    const value = start + step * instanceIndex;

    const tooGreat = maxValue && value > maxValue;
    if (tooGreat) {
        return "";
    }

    const stringValue = value.toString();
    const tooShort = minLength && stringValue.length < minLength;
    if (tooShort) {
        return "0".repeat(minLength - stringValue.length).concat(stringValue);
    }

    return stringValue;
}
